/**
 * Read-only pilot profile fields aligned with PilotProfileFormFields (mobile).
 */

import {
  buildTaskDescription,
  isPilotInterestTask,
  parseInterestedPilotsList,
  parseTaskDataRecord,
  taskMissionId,
} from "./tasksParse";

export const EMPTY_PILOT_PROFILE = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  homeAirport: "",
  aircraftTypes: "",
  picTime: "",
  willingToFlyDistance: "",
  additionalInfo: "",
  hearAboutUs: "",
  whyVolunteer: "",
  studentName: "",
  studentCity: "",
  notes: "",
};

export const EMPTY_PILOT_ACCOUNT = {
  profileId: "",
  status: "",
  role: "",
  email: "",
  associations: "",
  mobile: "",
  employeeId: "",
  locale: "",
  timezone: "",
  lastLogin: "",
  createdAt: "",
  updatedAt: "",
};

function pickString(obj, ...keys) {
  if (!obj || typeof obj !== "object") return "";
  for (const key of keys) {
    const value = obj[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return "";
}

function parseMaybeJson(value) {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function splitFullName(name) {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

function aircraftsToString(aircrafts) {
  if (!aircrafts) return "";
  if (typeof aircrafts === "string") return aircrafts.trim();
  if (!Array.isArray(aircrafts)) return "";
  return aircrafts
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (item && typeof item === "object") return String(item.name ?? "").trim();
      return "";
    })
    .filter((name) => name && name !== "Unspecified")
    .join(", ");
}

function splitNotesField(notesRaw, existing = {}) {
  const notesRawStr = String(notesRaw ?? "").trim();
  let additionalInfo = String(existing.additionalInfo ?? "").trim();
  let notes = String(existing.notes ?? "").trim();

  if (notesRawStr && !additionalInfo && !notes) {
    const parts = notesRawStr.split(/\n\n+/);
    if (parts.length >= 2) {
      additionalInfo = parts[0].trim();
      notes = parts.slice(1).join("\n\n").trim();
    } else {
      additionalInfo = notesRawStr;
    }
  } else if (notesRawStr && !additionalInfo) {
    additionalInfo = notesRawStr;
  }

  return { additionalInfo, notes };
}

function locationFromRecord(source) {
  if (!source || typeof source !== "object") {
    return { city: "", state: "" };
  }

  const loc = source.location;
  if (Array.isArray(loc) && loc.length > 0) {
    const first = loc[0];
    if (first && typeof first === "object") {
      return {
        city: pickString(first, "city", "name"),
        state: pickString(first, "state"),
      };
    }
  }

  if (loc && typeof loc === "object" && !Array.isArray(loc)) {
    return {
      city: pickString(loc, "city", "name"),
      state: pickString(loc, "state"),
    };
  }

  return {
    city: pickString(source, "city", "locationCity"),
    state: pickString(source, "state", "locationState"),
  };
}

function formatAssociationsDisplay(associations) {
  const list = Array.isArray(associations) ? associations : [];
  if (list.length === 0) return "";

  return list
    .map((item) => {
      if (!item || typeof item !== "object") return "";
      const role = pickString(item, "role");
      const entityType = pickString(item, "entityType");
      const entityName = pickString(item, "entityName");
      const entityId = pickString(item, "entityId");
      const parts = [role, entityType, entityName || entityId].filter(Boolean);
      return parts.join(" · ");
    })
    .filter(Boolean)
    .join("; ");
}

function extractAssociations(userData) {
  const user = userData && typeof userData === "object" ? userData : null;
  if (!user) return [];

  const profile = user.profile && typeof user.profile === "object" ? user.profile : null;
  const raw =
    user.association ??
    user.associations ??
    profile?.association ??
    profile?.associations ??
    [];

  return Array.isArray(raw) ? raw : [];
}

function isPrivilegedTaskProfileRole(role) {
  const normalized = String(role ?? "").trim().toLowerCase();
  return (
    normalized.includes("admin") ||
    normalized === "coordinator" ||
    normalized === "staff"
  );
}

function pilotIdFromRecord(record) {
  if (!record || typeof record !== "object") return null;
  const fromPilot =
    record.pilot && typeof record.pilot === "object"
      ? record.pilot.profileId ?? record.pilot.id
      : null;
  const id = record.pilotId ?? record.pilotProfileId ?? fromPilot;
  return id ? String(id).trim() : null;
}

function profileIdFromTaskProfiles(profiles) {
  if (!Array.isArray(profiles)) return null;

  const pilotRole = profiles.find((profile) =>
    String(profile?.role ?? "")
      .toLowerCase()
      .includes("pilot"),
  );
  if (pilotRole?.profileId) return String(pilotRole.profileId).trim();

  const userRole = profiles.find((profile) => {
    const role = String(profile?.role ?? "").trim().toLowerCase();
    return role === "user" || role === "pilot";
  });
  if (userRole?.profileId) return String(userRole.profileId).trim();

  const nonPrivileged = profiles.find(
    (profile) => !isPrivilegedTaskProfileRole(profile?.role),
  );
  if (nonPrivileged?.profileId) {
    return String(nonPrivileged.profileId).trim();
  }

  return null;
}

function mergePilotField(target, key, value) {
  if (value != null && String(value).trim()) {
    target[key] = String(value).trim();
  }
}

/** Map getUser AWSJSON (Profile shape) into pilot display fields. */
function mapUserRootToPilotFields(userData) {
  if (!userData || typeof userData !== "object") return { ...EMPTY_PILOT_PROFILE };

  const user = userData;
  const profile =
    user.profile && typeof user.profile === "object" ? user.profile : {};

  const pick = (...keys) => pickString(user, ...keys) || pickString(profile, ...keys);

  const nameParts = splitFullName(
    pick("name") || `${pick("firstName")} ${pick("lastName")}`.trim(),
  );

  const { city: locCity, state: locState } = locationFromRecord(user);
  const aircraftsParsed = parseMaybeJson(user.aircrafts ?? profile.aircrafts);

  const merged = { ...EMPTY_PILOT_PROFILE };
  mergePilotField(merged, "firstName", pick("firstName") || nameParts.firstName);
  mergePilotField(merged, "lastName", pick("lastName") || nameParts.lastName);
  mergePilotField(merged, "email", pick("email"));
  mergePilotField(
    merged,
    "phone",
    pick("phone", "phoneNumber") || pick("mobile"),
  );
  mergePilotField(merged, "city", pick("city", "locationCity") || locCity);
  mergePilotField(merged, "state", pick("state", "locationState") || locState);
  mergePilotField(merged, "homeAirport", pick("homeAirport", "home_airport"));
  mergePilotField(
    merged,
    "aircraftTypes",
    aircraftsToString(aircraftsParsed) || pick("aircraftTypes", "aircraft"),
  );
  mergePilotField(
    merged,
    "picTime",
    pick("pilot_in_command_hours", "picTime", "picHours"),
  );
  mergePilotField(
    merged,
    "willingToFlyDistance",
    pick("maxDistanceLimit", "willingToFlyDistance"),
  );
  mergePilotField(merged, "hearAboutUs", pick("marketingSource", "hearAboutUs"));
  mergePilotField(merged, "whyVolunteer", pick("interestReason", "whyVolunteer"));
  mergePilotField(merged, "studentName", pick("studentName"));
  mergePilotField(merged, "studentCity", pick("studentCity"));

  const notesRaw = pick("notes");
  const additionalDirect = pick("additionalInfo");
  if (additionalDirect) merged.additionalInfo = additionalDirect;
  if (notesRaw) {
    const splitNotes = splitNotesField(notesRaw, merged);
    mergePilotField(merged, "additionalInfo", splitNotes.additionalInfo);
    mergePilotField(merged, "notes", splitNotes.notes);
  }

  return merged;
}

function mergePilotProfiles(base, overlay) {
  const merged = { ...base };
  for (const [key, value] of Object.entries(overlay ?? {})) {
    if (value != null && String(value).trim()) {
      merged[key] = String(value).trim();
    }
  }
  return merged;
}
function pilotPayloadFromRecord(record) {
  if (!record || typeof record !== "object") return null;

  const hasPilotFields =
    record.firstName ||
    record.homeAirport ||
    record.pilot_in_command_hours ||
    record.aircrafts ||
    record.aircraftTypes;

  if (!hasPilotFields) return null;
  return record;
}

function collectPilotSources(userData, taskData) {
  const sources = [];
  const user = userData && typeof userData === "object" ? userData : null;
  const task = taskData && typeof taskData === "object" ? taskData : null;

  if (task) {
    if (task.pilot && typeof task.pilot === "object") sources.push(task.pilot);
    if (task.pilotProfile && typeof task.pilotProfile === "object") {
      sources.push(task.pilotProfile);
    }

    const parsedTaskData = parseTaskDataRecord(task);
    if (parsedTaskData && typeof parsedTaskData === "object") {
      if (parsedTaskData.pilot && typeof parsedTaskData.pilot === "object") {
        sources.push(parsedTaskData.pilot);
      }
    }

    const profiles = Array.isArray(task.profiles) ? task.profiles : [];
    const pilotAssoc = profiles.find((profile) =>
      String(profile?.role ?? "")
        .toLowerCase()
        .includes("pilot"),
    );
    if (pilotAssoc) {
      sources.push(pilotAssoc);
    } else if (isPilotInterestTask(task)) {
      const nonAdminProfile = profiles.find(
        (profile) => !isPrivilegedTaskProfileRole(profile?.role),
      );
      if (nonAdminProfile) sources.push(nonAdminProfile);
    }
  }

  if (user) {
    sources.push(user);
    if (user.profile && typeof user.profile === "object") sources.push(user.profile);
    if (user.pilot && typeof user.pilot === "object") sources.push(user.pilot);
    if (user.pilotProfile && typeof user.pilotProfile === "object") {
      sources.push(user.pilotProfile);
    }

    const preference = parseMaybeJson(user.preference);
    if (preference && typeof preference === "object") {
      sources.push(preference);
      if (preference.pilot) sources.push(preference.pilot);
      if (preference.pilotProfile) sources.push(preference.pilotProfile);
    }

    const performance = parseMaybeJson(user.performance);
    if (performance && typeof performance === "object") {
      sources.push(performance);
      if (performance.pilot) sources.push(performance.pilot);
    }

    const aircrafts = parseMaybeJson(user.aircrafts);
    if (aircrafts) {
      sources.push({ aircrafts });
    }
  }

  return sources.filter(Boolean);
}

function pilotRecordId(pilot) {
  if (pilot == null) return "";
  if (typeof pilot === "string") return pilot.trim();
  if (typeof pilot !== "object") return "";
  return String(pilot.profileId ?? pilot.id ?? pilot.pilotId ?? "").trim();
}

function pilotRecordName(pilot) {
  if (!pilot || typeof pilot !== "object") return "";
  const fromName = pickString(pilot, "name");
  if (fromName) return fromName;
  return `${pickString(pilot, "firstName")} ${pickString(pilot, "lastName")}`.trim();
}

function namesLooselyMatch(a, b) {
  const normalize = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
  const left = normalize(a);
  const right = normalize(b);
  if (!left || !right) return false;
  if (left === right || left.includes(right) || right.includes(left)) return true;

  const leftFirst = left.split(" ")[0];
  const rightFirst = right.split(" ")[0];
  return leftFirst.length > 2 && leftFirst === rightFirst;
}

/** Profile IDs on the task itself (assignee / owner), not the interested pilot. */
function getTaskAssigneeProfileIds(task) {
  const ids = new Set();
  const profiles = Array.isArray(task?.profiles) ? task.profiles : [];

  for (const profile of profiles) {
    const id = profile?.profileId ?? profile?.id;
    if (id != null && String(id).trim()) ids.add(String(id).trim());
  }

  const taskData = parseTaskDataRecord(task);
  if (taskData && typeof taskData === "object") {
    for (const key of [
      "profileId",
      "loggedProfileId",
      "assigneeProfileId",
      "ownerProfileId",
      "recipientProfileId",
    ]) {
      const value = taskData[key];
      if (value != null && String(value).trim()) ids.add(String(value).trim());
    }
  }

  return ids;
}

function isAssigneeProfileId(task, profileId) {
  if (profileId == null || !String(profileId).trim()) return false;
  return getTaskAssigneeProfileIds(task).has(String(profileId).trim());
}

function extractPilotNameFromDescription(task) {
  const description = buildTaskDescription(task);
  if (!description) return "";

  const patterns = [
    /^([A-Za-z][A-Za-z\s.'-]+?)\s+has expressed interest/i,
    /^([A-Za-z][A-Za-z\s.'-]+?)\s+is interested/i,
    /^([A-Za-z][A-Za-z\s.'-]+?)\s+has shown interest/i,
    /pilot\s+([A-Za-z][A-Za-z\s.'-]+?)\s+(has|is)/i,
  ];

  for (const pattern of patterns) {
    const match = description.match(pattern);
    if (match?.[1]) return String(match[1]).trim();
  }

  return "";
}

function pilotIdFromTaskDataRecord(taskData, excludeIds = new Set()) {
  if (!taskData || typeof taskData !== "object") return null;

  const accept = (value) => {
    const id = value != null ? String(value).trim() : "";
    if (!id || excludeIds.has(id)) return null;
    return id;
  };

  if (taskData.interestedPilot && typeof taskData.interestedPilot === "object") {
    const fromInterested = accept(
      taskData.interestedPilot.profileId ??
        taskData.interestedPilot.id ??
        taskData.interestedPilot.pilotId,
    );
    if (fromInterested) return fromInterested;
  }

  const fromPilotObject = pilotIdFromRecord(taskData);
  if (fromPilotObject) {
    const accepted = accept(fromPilotObject);
    if (accepted) return accepted;
  }

  for (const key of [
    "pilotId",
    "pilotProfileId",
    "interestedPilotId",
    "interestedPilotProfileId",
  ]) {
    const accepted = accept(taskData[key]);
    if (accepted) return accepted;
  }

  return null;
}

function resolvePilotFromInterestedList(pilots, preferredName, excludeIds = new Set()) {
  if (!Array.isArray(pilots) || pilots.length === 0) return null;

  const trimmedName = String(preferredName ?? "").trim();
  const candidates = pilots.filter((pilot) => {
    const profileId = pilotRecordId(pilot);
    return profileId && !excludeIds.has(profileId);
  });

  if (candidates.length === 0) return null;

  if (trimmedName) {
    const byName = candidates.find((pilot) =>
      namesLooselyMatch(pilotRecordName(pilot), trimmedName),
    );
    if (byName) {
      const profileId = pilotRecordId(byName);
      if (profileId) {
        return {
          profileId,
          name: pilotRecordName(byName) || trimmedName,
        };
      }
    }
  }

  if (candidates.length === 1) {
    const pilot = candidates[0];
    const profileId = pilotRecordId(pilot);
    if (profileId) {
      return {
        profileId,
        name:
          pilotRecordName(pilot) ||
          (typeof pilot === "string" ? trimmedName : "") ||
          trimmedName,
      };
    }
  }

  return null;
}

export function extractPilotProfileIdFromTask(task) {
  if (!task || typeof task !== "object") return null;

  const assigneeIds = getTaskAssigneeProfileIds(task);
  const taskData = parseTaskDataRecord(task);
  const pilotInterest = isPilotInterestTask(task);

  if (taskData) {
    const fromData = pilotIdFromTaskDataRecord(taskData, assigneeIds);
    if (fromData) return fromData;
  }

  const topLevelId = pilotIdFromRecord(task);
  if (topLevelId && !assigneeIds.has(topLevelId)) return topLevelId;

  if (!pilotInterest) {
    const fromProfiles = profileIdFromTaskProfiles(task.profiles);
    if (fromProfiles && !assigneeIds.has(fromProfiles)) return fromProfiles;
  }

  return null;
}

export function extractPilotDisplayNameFromTask(task) {
  const taskData = parseTaskDataRecord(task);
  const pilotInterest = isPilotInterestTask(task);

  if (taskData && typeof taskData === "object") {
    const fromPilotObject =
      taskData.pilot && typeof taskData.pilot === "object"
        ? pickString(taskData.pilot, "name")
        : "";
    const fromInterestedPilot =
      taskData.interestedPilot && typeof taskData.interestedPilot === "object"
        ? pilotRecordName(taskData.interestedPilot)
        : "";
    const fromData =
      pickString(taskData, "pilotName") ||
      fromInterestedPilot ||
      fromPilotObject ||
      `${pickString(taskData, "firstName")} ${pickString(taskData, "lastName")}`.trim();
    if (fromData) return fromData;
  }

  if (pilotInterest) {
    const fromDescription = extractPilotNameFromDescription(task);
    if (fromDescription) return fromDescription;
    return "";
  }

  const profiles = Array.isArray(task?.profiles) ? task.profiles : [];
  const pilotAssoc = profiles.find((profile) =>
    String(profile?.role ?? "")
      .toLowerCase()
      .includes("pilot"),
  );
  if (pilotAssoc?.name) return String(pilotAssoc.name).trim();

  return "";
}

/** Resolve pilot profileId from task, falling back to mission.interestedPilots when needed. */
export async function resolvePilotProfileFromTask(
  task,
  idToken,
  getMissionByIdFn,
) {
  const directName = extractPilotDisplayNameFromTask(task);
  const assigneeIds = getTaskAssigneeProfileIds(task);

  if (isPilotInterestTask(task)) {
    const missionId = taskMissionId(task);
    if (missionId && idToken && getMissionByIdFn) {
      try {
        const res = await getMissionByIdFn(missionId, idToken);
        let mission = res?.data;
        while (typeof mission === "string") {
          try {
            mission = JSON.parse(mission);
          } catch {
            break;
          }
        }

        const pilots = parseInterestedPilotsList(mission?.interestedPilots);
        const resolved = resolvePilotFromInterestedList(
          pilots,
          directName,
          assigneeIds,
        );
        if (resolved?.profileId) return resolved;
      } catch {
        // Mission lookup is best-effort when task.data lacks pilotId.
      }
    }

    const taskData = parseTaskDataRecord(task);
    const fromData = taskData
      ? pilotIdFromTaskDataRecord(taskData, assigneeIds)
      : null;
    if (fromData) return { profileId: fromData, name: directName };

    const topLevelId = pilotIdFromRecord(task);
    if (topLevelId && !assigneeIds.has(topLevelId)) {
      return { profileId: topLevelId, name: directName };
    }

    return { profileId: null, name: directName };
  }

  const directId = extractPilotProfileIdFromTask(task);
  if (directId && !isAssigneeProfileId(task, directId)) {
    return { profileId: directId, name: directName };
  }

  return { profileId: null, name: directName };
}

export function formatPilotDateTime(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).trim();
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Account / identity fields from getUser AWSJSON. */
export function mapToPilotAccountDisplay(userData, options = {}) {
  const user = userData && typeof userData === "object" ? userData : {};
  const profile = user.profile && typeof user.profile === "object" ? user.profile : {};
  let associations = extractAssociations(userData);

  if (options.pilotContext) {
    const pilotAssociations = associations.filter((item) => {
      const role = String(item?.role ?? "").trim().toLowerCase();
      return role === "pilot" || role === "user";
    });
    if (pilotAssociations.length) associations = pilotAssociations;
  }

  const roleFromAssoc = associations
    .map((item) => pickString(item, "role"))
    .filter(Boolean)
    .join(", ");

  return {
    profileId: pickString(user, "profileId") || pickString(profile, "profileId"),
    status: pickString(user, "status") || pickString(profile, "status"),
    email: pickString(user, "email") || pickString(profile, "email"),
    role:
      roleFromAssoc ||
      pickString(user, "role") ||
      pickString(profile, "role"),
    associations: formatAssociationsDisplay(associations),
    mobile: pickString(user, "mobile") || pickString(profile, "mobile"),
    employeeId:
      pickString(user, "employeeId") || pickString(profile, "employeeId"),
    locale: pickString(user, "locale") || pickString(profile, "locale"),
    timezone:
      pickString(user, "timezone") || pickString(profile, "timezone"),
    lastLogin: formatPilotDateTime(
      pickString(user, "lastLogin") || pickString(profile, "lastLogin"),
    ),
    createdAt: formatPilotDateTime(
      pickString(user, "createdAt", "created") ||
        pickString(profile, "createdAt", "created"),
    ),
    updatedAt: formatPilotDateTime(
      pickString(user, "updatedAt", "updated") ||
        pickString(profile, "updatedAt", "updated"),
    ),
  };
}

/** Map task-only pilot hints (does not include logged-in user metadata). */
function mapTaskToPilotFields(taskData) {
  const sources = collectPilotSources(null, taskData);
  const pilotSources = sources
    .map(pilotPayloadFromRecord)
    .filter(Boolean)
    .concat(sources);

  const merged = { ...EMPTY_PILOT_PROFILE };
  let notesRaw = "";

  for (const source of pilotSources) {
    if (!source || typeof source !== "object") continue;

    const nameParts = splitFullName(
      pickString(source, "name") ||
        `${pickString(source, "firstName")} ${pickString(source, "lastName")}`.trim(),
    );

    mergePilotField(
      merged,
      "firstName",
      pickString(source, "firstName") || nameParts.firstName,
    );
    mergePilotField(
      merged,
      "lastName",
      pickString(source, "lastName") || nameParts.lastName,
    );
    mergePilotField(merged, "email", pickString(source, "email"));

    const phone = pickString(source, "phone", "phoneNumber");
    const mobile = pickString(source, "mobile");
    mergePilotField(merged, "phone", phone || mobile);

    const { city, state } = locationFromRecord(source);
    mergePilotField(merged, "city", city);
    mergePilotField(merged, "state", state);
    mergePilotField(
      merged,
      "homeAirport",
      pickString(source, "homeAirport", "home_airport"),
    );

    const aircraftsParsed = parseMaybeJson(source.aircrafts);
    mergePilotField(
      merged,
      "aircraftTypes",
      aircraftsToString(aircraftsParsed ?? source.aircrafts) ||
        pickString(source, "aircraftTypes", "aircraft"),
    );
    mergePilotField(
      merged,
      "picTime",
      pickString(source, "picTime", "pilot_in_command_hours", "picHours"),
    );
    mergePilotField(
      merged,
      "willingToFlyDistance",
      pickString(source, "willingToFlyDistance", "maxDistanceLimit"),
    );
    mergePilotField(
      merged,
      "hearAboutUs",
      pickString(source, "hearAboutUs", "marketingSource"),
    );
    mergePilotField(
      merged,
      "whyVolunteer",
      pickString(source, "whyVolunteer", "interestReason"),
    );
    mergePilotField(merged, "studentName", pickString(source, "studentName"));
    mergePilotField(merged, "studentCity", pickString(source, "studentCity"));

    const noteValue = pickString(source, "notes");
    if (noteValue) notesRaw = noteValue;

    mergePilotField(merged, "additionalInfo", pickString(source, "additionalInfo"));
  }

  const splitNotes = splitNotesField(notesRaw, merged);
  mergePilotField(merged, "additionalInfo", splitNotes.additionalInfo);
  mergePilotField(merged, "notes", splitNotes.notes);

  return merged;
}

/** Map API / task payloads into read-only pilot profile display fields. */
export function mapToPilotProfileDisplay(userData, taskData) {
  if (userData && typeof userData === "object") {
    const fromUser = mapUserRootToPilotFields(userData);
    if (taskData) {
      return mergePilotProfiles(fromUser, mapTaskToPilotFields(taskData));
    }
    return fromUser;
  }

  return mapTaskToPilotFields(taskData);
}

export function pilotProfileDisplayName(profile, userData) {
  const first = String(profile?.firstName ?? "").trim();
  const last = String(profile?.lastName ?? "").trim();
  const full = `${first} ${last}`.trim();
  if (full) return full;

  const user = userData && typeof userData === "object" ? userData : null;
  const fromUser =
    pickString(user, "name") ||
    pickString(user?.profile, "name");
  if (fromUser) return fromUser;

  return "Pilot profile";
}

export function hasPilotProfileContent(profile) {
  if (!profile || typeof profile !== "object") return false;
  return Object.values(profile).some((value) => String(value ?? "").trim());
}
